/**
 * Módulo Asistente Laboral — pestañas en registro único.
 * Sidebar selectivo; validación Check ID en /laboral/validar-inscribir (sin saltar a Trámites).
 */
import { Navigate } from "react-router-dom";
import PanelGeneral from "@/pages/PanelGeneral";
import Clientes from "@/pages/Clientes";
import Ayuda from "@/pages/Ayuda";
import DashboardLaboral from "@/pages/laboral/DashboardLaboral";
import RegistrosPatronales from "@/pages/laboral/RegistrosPatronales";
import ConfrontaTrabajadoresImss from "@/pages/laboral/ConfrontaTrabajadoresImss";
import ControlRepse from "@/pages/laboral/ControlRepse";
import CatalogoTrabajadores from "@/pages/laboral/CatalogoTrabajadores";
import ExpedientesLaborales from "@/pages/laboral/ExpedientesLaborales";
import ConfrontaEmisionesImss from "@/pages/laboral/ConfrontaEmisionesImss";
import LaboralValidarInscribir from "@/pages/laboral/LaboralValidarInscribir";
import DatosMaestrosHub from "@/pages/DatosMaestrosHub";
import DatosMaestrosValidadorCheckIdPage from "@/pages/datosMaestros/DatosMaestrosValidadorCheckIdPage";
import DatosMaestrosValidacionRfcSatPage from "@/pages/datosMaestros/DatosMaestrosValidacionRfcSatPage";
import { VALIDADOR_DATOS_CHECK_ID_LABEL } from "@/lib/checkIdLabels";
import {
  DATOS_MAESTROS_CHECK_ID_PATH,
  DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
} from "@/modules/datosMaestros/datosMaestrosHub";
import { buildModuloFromTabs, createModuloTab } from "@/modules/moduloTabRegistry";
import { TRAMITES_LEGACY_REDIRECT_TARGETS } from "@/modules/tramites/tramitesService";
import {
  filterLaboralHubCards,
  filterLaboralNavItems,
  LABORAL_DELEGATION_PATHS,
} from "@/modules/laboralNavPolicy";

export const MODULO_LABORAL_ID = "asistente_laboral";

export const MODULO_LABORAL_CARD = {
  id: MODULO_LABORAL_ID,
  title: "Asistente Laboral y REPSE",
  description: "Clientes, patronales, catálogo, REPSE y reportes IMSS.",
  entryPath: "/laboral/inicio",
  path: "/laboral/inicio",
  route: "/laboral/inicio",
};

const CONTEXT_LABEL = "Asistente Laboral y REPSE";

const SEC_BASE = "1. Base del cliente";
const SEC_EXPEDIENTE = "2. Expediente trabajadores";
const SEC_REPSE = "3. Obra y REPSE";
const SEC_REPORTES = "4. Reportes IMSS";

export const MODULO_LABORAL_LOCKED_MESSAGE =
  "Este módulo Premium requiere una licencia activa. Puede solicitar su cotización y activación " +
  "en la sección de Licencias mediante el botón «Solicitar Licencia».";

const LABORAL_TABS = [
  createModuloTab({
    index: true,
    path: "/",
    label: "Panel General",
    iconKey: "panel",
    end: true,
    showInNav: false,
    testid: "nav-panel-general",
    component: <PanelGeneral />,
  }),
  createModuloTab({
    path: "/laboral/inicio",
    label: "Panel de control laboral",
    description: "Alertas operativas y de cumplimiento del despacho.",
    iconKey: "panel",
    navSection: SEC_BASE,
    navStep: 0,
    testid: "nav-laboral-dashboard",
    component: <DashboardLaboral />,
  }),
  createModuloTab({
    path: "/clientes",
    label: "Clientes",
    description: "Seleccione o registre la empresa (RFC) de trabajo.",
    iconKey: "clients",
    navSection: SEC_BASE,
    navStep: 1,
    hubOnlyNav: true,
    component: <Clientes />,
  }),
  createModuloTab({
    path: "/datos-maestros",
    label: "Datos Maestros",
    description: "Catálogo fiscal compartido: empresas, FIEL y utilidades SAT base.",
    iconKey: "database",
    navSection: SEC_BASE,
    navStep: 1,
    testid: "nav-datos-maestros",
    component: <DatosMaestrosHub />,
  }),
  createModuloTab({
    path: DATOS_MAESTROS_CHECK_ID_PATH,
    label: VALIDADOR_DATOS_CHECK_ID_LABEL,
    iconKey: "validate",
    showInNav: false,
    hubOnlyNav: true,
    testid: "nav-datos-maestros-check-id",
    component: <DatosMaestrosValidadorCheckIdPage />,
  }),
  createModuloTab({
    path: DATOS_MAESTROS_VALIDACION_RFC_SAT_PATH,
    label: "Validación de RFC SAT",
    iconKey: "validate",
    showInNav: false,
    hubOnlyNav: true,
    testid: "nav-datos-maestros-validacion-rfc-sat",
    component: <DatosMaestrosValidacionRfcSatPage />,
  }),
  createModuloTab({
    path: "/registros-patronales-certificados",
    label: "Registros patronales",
    description: "Matriz y obras: registro patronal, certificado e IDSE.",
    iconKey: "shield",
    breadcrumbModule: CONTEXT_LABEL,
    navSection: SEC_BASE,
    navStep: 1,
    testid: "nav-registros-patronales",
    component: <RegistrosPatronales />,
  }),
  createModuloTab({
    path: "/catalogo-trabajadores",
    label: "Catálogo de trabajadores",
    description: "Alta manual, importación Excel/PDF o carga desde SAT.",
    iconKey: "users",
    breadcrumbModule: CONTEXT_LABEL,
    navSection: SEC_EXPEDIENTE,
    navStep: 3,
    testid: "nav-catalogo-trabajadores",
    component: <CatalogoTrabajadores />,
  }),
  createModuloTab({
    path: "/expedientes-laborales",
    label: "Expedientes laborales",
    iconKey: "folder",
    breadcrumbModule: CONTEXT_LABEL,
    navSection: SEC_EXPEDIENTE,
    navStep: 3,
    testid: "nav-expedientes-laborales",
    showInNav: false,
    component: <ExpedientesLaborales />,
  }),
  createModuloTab({
    path: "/laboral/validar-inscribir",
    label: VALIDADOR_DATOS_CHECK_ID_LABEL,
    description: "Check ID del trabajador: RFC, CURP, NSS y consistencia antes de inscripción IMSS.",
    iconKey: "validate",
    breadcrumbModule: CONTEXT_LABEL,
    navSection: SEC_EXPEDIENTE,
    navStep: 4,
    testid: "nav-laboral-validar-inscribir",
    hubOnlyNav: true,
    component: <LaboralValidarInscribir />,
  }),
  /* Delegadas a Trámites Fiscales — redirect global vía TRAMITES_LEGACY_REDIRECT_TARGETS. */
  createModuloTab({
    path: "/control-repse",
    label: "Control REPSE",
    description: "Registre obra o contrato, altas/bajas y asigne trabajadores.",
    iconKey: "briefcase",
    breadcrumbModule: CONTEXT_LABEL,
    navSection: SEC_REPSE,
    navStep: 5,
    testid: "nav-control-repse",
    component: <ControlRepse />,
  }),
  createModuloTab({
    path: "/confronta-trabajadores-imss",
    label: "Declaraciones cuatrimestrales",
    description: "Genere SISUB e ICSOE por obra y periodo cuatrimestral.",
    iconKey: "compare",
    breadcrumbModule: CONTEXT_LABEL,
    navSection: SEC_REPORTES,
    navStep: 6,
    testid: "nav-confronta-trabajadores-imss",
    component: <ConfrontaTrabajadoresImss />,
  }),
  createModuloTab({
    path: "/confronta-emisiones-imss",
    label: "Confronta IMSS",
    description: "Compare la descarga IMSS contra su catálogo interno.",
    iconKey: "compare",
    breadcrumbModule: CONTEXT_LABEL,
    navSection: SEC_REPORTES,
    navStep: 7,
    testid: "nav-confronta-emisiones-imss",
    component: <ConfrontaEmisionesImss />,
  }),
  createModuloTab({
    path: "/tramites-fiscales",
    label: "Trámites Fiscales",
    iconKey: "tramites",
    showInNav: false,
  }),
  createModuloTab({
    path: "/gestion-datos",
    label: "Gestión de Datos",
    iconKey: "database",
    showInNav: false,
    hubOnlyNav: true,
  }),
  createModuloTab({
    path: "/ayuda",
    label: "Ayuda y Manuales",
    iconKey: "help",
    hubOnlyNav: true,
    showInNav: false,
    component: <Ayuda />,
  }),
];

const LABORAL_EXTRA_ROUTES = [
  { path: "validacion-datos-trabajadores", element: <Navigate to="/catalogo-trabajadores" replace /> },
  { path: "reportes-expedientes", element: <Navigate to="/catalogo-trabajadores?tab=registrados" replace /> },
  ...Object.keys(TRAMITES_LEGACY_REDIRECT_TARGETS).map((legacyPath) => ({
    path: legacyPath.replace(/^\//, ""),
    element: <Navigate to={TRAMITES_LEGACY_REDIRECT_TARGETS[legacyPath]} replace />,
  })),
];

const _MODULO_LABORAL_BASE = buildModuloFromTabs({
  id: MODULO_LABORAL_ID,
  card: MODULO_LABORAL_CARD,
  contextLabel: CONTEXT_LABEL,
  tabs: LABORAL_TABS,
  extraRoutes: LABORAL_EXTRA_ROUTES,
  lockedMessage: MODULO_LABORAL_LOCKED_MESSAGE,
});

export const MODULO_LABORAL = {
  ..._MODULO_LABORAL_BASE,
  getNavItems: () => filterLaboralNavItems(_MODULO_LABORAL_BASE.getNavItems()),
  getHubCards: () => filterLaboralHubCards(_MODULO_LABORAL_BASE.getHubCards()),
  isPathAllowed: (pathname) => {
    const path = String(pathname || "").split("?")[0];
    if (LABORAL_DELEGATION_PATHS.has(path)) return true;
    return _MODULO_LABORAL_BASE.isPathAllowed(pathname);
  },
};

export const getModuloLaboralNavItems = () => MODULO_LABORAL.getNavItems();
export const getModuloLaboralPaths = () => MODULO_LABORAL.getPaths();
export const getModuloLaboralDefaultRoute = () => MODULO_LABORAL.getDefaultRoute();
export const isModuloLaboralPath = (pathname) => MODULO_LABORAL.isPathAllowed(pathname);
export const getModuloLaboralContextLabel = () => MODULO_LABORAL.getContextLabel();
export const getModuloLaboralRoutes = () => MODULO_LABORAL.getRoutes();
